<template>
  <nav data-global-navbar class="bg-accent border-b border-green-700 relative z-40 transition-transform duration-300"
    :class="{ '-translate-y-full': isNavbarHidden }">
    <!-- Loading overlay -->
    <div v-if="isNavigating" class="fixed inset-0 z-[9999] flex items-center justify-center bg-white bg-opacity-90 pointer-events-auto">
      <Loader size="medium" />
    </div>

    <div class="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex flex-col">
        <div class="flex justify-between items-center py-6 relative">
          <button @click="showMobileMenu = !showMobileMenu"
            class="block xl:!hidden p-2 text-white hover:text-green-200 transition-colors z-10 absolute left-0"
            aria-label="Toggle menu">
            <svg v-if="!showMobileMenu" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div class="hidden xl:flex items-center flex-1 min-w-0 justify-evenly">
            <a href="/" class="flex items-center justify-center flex-shrink-0 m-0 p-0 self-center" data-navbar-logo>
              <img src="/logos/Horizontal Logo/No Background Black Logo Horizontal.png" alt="Barry & Bonna Publishing"
                class="h-[75px] w-auto max-w-[375px] brightness-0 invert flex-shrink-0 m-0 p-0 block" />
            </a>
            <a href="/books"
              :class="['font-marketing transition-colors whitespace-nowrap text-sm xl:text-base flex-shrink-0', isActive('/books') ? 'text-white border-b-2 border-white pb-1' : 'text-white hover:text-green-200']">Books</a>
            <a href="/authors"
              :class="['font-marketing transition-colors whitespace-nowrap text-sm xl:text-base flex-shrink-0', isActive('/authors') ? 'text-white border-b-2 border-white pb-1' : 'text-white hover:text-green-200']">Authors</a>
            <a href="/events"
              :class="['font-marketing transition-colors whitespace-nowrap text-sm xl:text-base flex-shrink-0', isActive('/events') ? 'text-white border-b-2 border-white pb-1' : 'text-white hover:text-green-200']">Events</a>
            <a href="/submissions"
              :class="['font-marketing transition-colors whitespace-nowrap text-sm xl:text-base flex-shrink-0', isActive('/submissions') ? 'text-white border-b-2 border-white pb-1' : 'text-white hover:text-green-200']">Submission</a>
            <a href="/press"
              :class="['font-marketing transition-colors whitespace-nowrap text-sm xl:text-base flex-shrink-0', isActive('/press') ? 'text-white border-b-2 border-white pb-1' : 'text-white hover:text-green-200']">Press</a>
            <a href="/careers"
              :class="['font-marketing transition-colors whitespace-nowrap text-sm xl:text-base flex-shrink-0', isActive('/careers') ? 'text-white border-b-2 border-white pb-1' : 'text-white hover:text-green-200']">Careers</a>
            <a v-if="currentUser?.is_admin" href="/admin"
              :class="['font-marketing transition-colors whitespace-nowrap text-sm xl:text-base flex-shrink-0', isActive('/admin') ? 'text-white border-b-2 border-white pb-1' : 'text-white hover:text-green-200']">Admin</a>
            <div class="flex-shrink-0">
              <LanguageSelector />
            </div>
            <button @click="$emit('open-search')"
              class="p-2 text-white hover:text-green-200 transition-colors flex-shrink-0" aria-label="Search">
              <svg class="w-[25.55px] h-[25.55px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
            <a href="/cart"
              class="relative p-2 text-white hover:text-green-200 transition-colors flex-shrink-0 xl:ml-4 2xl:ml-6"
              aria-label="Shopping cart">
              <svg class="w-[25.55px] h-[25.55px] text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span v-if="cartItemCount > 0"
                class="absolute top-0 right-0 bg-white text-accent text-xs font-marketing font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {{ cartItemCount }}
              </span>
            </a>
            <div class="relative flex-shrink-0 xl:ml-4 2xl:ml-6" data-navbar-avatar>
              <button @click="showUserMenu = !showUserMenu"
                class="p-2 text-white hover:text-green-200 transition-colors rounded-full focus:outline-none focus:ring-2 focus:ring-green-300"
                aria-label="User account">
                <div v-if="currentUser?.photo_url"
                  class="w-[34.06px] h-[34.06px] rounded-full overflow-hidden border-2 border-green-300">
                  <img :src="currentUser.photo_url"
                    :alt="`${currentUser.first_name} ${currentUser.last_name}` || 'User'"
                    class="w-full h-full object-cover" />
                </div>
                <svg v-else class="w-[25.55px] h-[25.55px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </button>
              <div v-if="showUserMenu"
                class="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-creme-300 py-1 z-50"
                data-navbar-dropdown>
                <template v-if="isAuthenticated">
                  <div v-if="currentUser" class="px-4 py-2 border-b border-creme-200 mb-1">
                    <p class="text-xs text-gray-500 font-sans mb-1">Signed in as</p>
                    <p class="text-sm font-marketing font-semibold text-gray-900 truncate">{{ currentUser.first_name &&
                      currentUser.last_name ? `${currentUser.first_name} ${currentUser.last_name}` : currentUser.email
                      }}</p>
                    <p v-if="currentUser.first_name && currentUser.last_name"
                      class="text-xs text-gray-500 font-sans truncate">{{
                        currentUser.email }}</p>
                  </div>
                  <a href="/profile" @click="showUserMenu = false"
                    class="block px-4 py-2 text-sm text-gray-900 hover:bg-creme-100 transition-colors font-marketing">
                    Purchase History
                  </a>
                  <a href="/my-library" @click="showUserMenu = false"
                    class="block px-4 py-2 text-sm text-gray-900 hover:bg-creme-100 transition-colors font-marketing">
                    My Library
                  </a>
                  <a href="/settings" @click="showUserMenu = false"
                    class="block px-4 py-2 text-sm text-gray-900 hover:bg-creme-100 transition-colors font-marketing">
                    Account Settings
                  </a>
                  <a v-if="currentUser?.is_admin" href="/admin" @click="showUserMenu = false"
                    class="block px-4 py-2 text-sm text-gray-900 hover:bg-creme-100 transition-colors font-marketing">
                    Admin Dashboard
                  </a>
                  <button @click="logout"
                    class="block w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-creme-100 transition-colors font-marketing">
                    Logout
                  </button>
                </template>
                <template v-else>
                  <a href="/login" @click="showUserMenu = false"
                    class="block px-4 py-2 text-sm text-gray-900 hover:bg-creme-100 transition-colors font-marketing">
                    Login
                  </a>
                  <a href="/register" @click="showUserMenu = false"
                    class="block px-4 py-2 text-sm text-gray-900 hover:bg-creme-100 transition-colors font-marketing">
                    Register
                  </a>
                </template>
              </div>
            </div>
          </div>

          <div v-if="!showSearchBar" class="xl:hidden flex items-center w-full relative">
            <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
              <a href="/" class="flex items-center pointer-events-auto" aria-label="Home">
                <img src="/logos/Horizontal Logo/No Background Black Logo Horizontal.png" alt="Barry & Bonna Publishing"
                  class="h-12 w-[210px] object-contain brightness-0 invert flex-shrink-0" />
              </a>
            </div>
            <div class="absolute right-0 flex items-center gap-0 z-10">
              <a href="/cart" class="relative p-2 -mr-2 text-white hover:text-green-200 transition-colors"
                aria-label="Shopping cart">
                <svg class="w-[25.55px] h-[25.55px] text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span v-if="cartItemCount > 0"
                  class="absolute top-0 right-0 bg-white text-accent text-xs font-marketing font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {{ cartItemCount }}
                </span>
              </a>
              <div class="relative">
                <button @click="showUserMenu = !showUserMenu"
                  class="p-2 text-white hover:text-green-200 transition-colors rounded-full focus:outline-none focus:ring-2 focus:ring-green-300"
                  aria-label="User account">
                  <div v-if="currentUser?.photo_url"
                    class="w-[34.06px] h-[34.06px] rounded-full overflow-hidden border-2 border-green-300">
                    <img :src="currentUser.photo_url"
                      :alt="`${currentUser.first_name} ${currentUser.last_name}` || 'User'"
                      class="w-full h-full object-cover" />
                  </div>
                  <svg v-else class="w-[25.55px] h-[25.55px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </button>
                <div v-if="showUserMenu"
                  class="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-creme-300 py-1 z-50"
                  data-navbar-dropdown>
                  <template v-if="isAuthenticated">
                    <div v-if="currentUser" class="px-4 py-2 border-b border-creme-200 mb-1">
                      <p class="text-xs text-gray-500 font-sans mb-1">Signed in as</p>
                      <p class="text-sm font-marketing font-semibold text-gray-900 truncate">{{ currentUser.first_name
                        && currentUser.last_name ? `${currentUser.first_name} ${currentUser.last_name}` :
                        currentUser.email }}</p>
                      <p v-if="currentUser.first_name && currentUser.last_name"
                        class="text-xs text-gray-500 font-sans truncate">{{
                          currentUser.email }}</p>
                    </div>
                    <a href="/profile" @click="showUserMenu = false"
                      class="block px-4 py-2 text-sm text-gray-900 hover:bg-creme-100 transition-colors font-marketing">
                      Purchase History
                    </a>
                    <a href="/my-library" @click="showUserMenu = false"
                      class="block px-4 py-2 text-sm text-gray-900 hover:bg-creme-100 transition-colors font-marketing">
                      My Library
                    </a>
                    <a href="/settings" @click="showUserMenu = false"
                      class="block px-4 py-2 text-sm text-gray-900 hover:bg-creme-100 transition-colors font-marketing">
                      Account Settings
                    </a>
                    <a v-if="currentUser?.is_admin" href="/admin" @click="showUserMenu = false"
                      class="block px-4 py-2 text-sm text-gray-900 hover:bg-creme-100 transition-colors font-marketing">
                      Admin Dashboard
                    </a>
                    <button @click="logout"
                      class="block w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-creme-100 transition-colors font-marketing">
                      Logout
                    </button>
                  </template>
                  <template v-else>
                    <a href="/login" @click="showUserMenu = false"
                      class="block px-4 py-2 text-sm text-gray-900 hover:bg-creme-100 transition-colors font-marketing">
                      Login
                    </a>
                    <a href="/register" @click="showUserMenu = false"
                      class="block px-4 py-2 text-sm text-gray-900 hover:bg-creme-100 transition-colors font-marketing">
                      Register
                    </a>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-show="showMobileMenu" class="xl:hidden fixed inset-0 z-[9998] flex pointer-events-auto">
      <div class="fixed inset-0 bg-black bg-opacity-50" @click="showMobileMenu = false"></div>
      <div class="relative z-10 bg-white w-4/5 max-w-sm h-full overflow-y-auto mobile-menu" data-navbar-dropdown>
        <div class="px-4 pt-6 pb-4 space-y-2">
          <button @click="showMobileMenu = false" class="mb-4 p-2 text-primary hover:text-green-700 transition-colors"
            aria-label="Close menu">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div class="pb-4">
            <div class="flex items-center border-b-2 border-gray-300 pb-0">
              <input type="text" v-model="mobileSearchQuery" placeholder="Search books, authors..."
                @keydown.enter="handleMobileSearch"
                class="flex-1 bg-transparent text-primary placeholder-gray-400 border-none outline-none text-base font-sans mr-4">
              <div class="flex items-center">
                <button v-if="mobileSearchQuery.trim()" type="button" @click="clearMobileSearch"
                  class="p-2 text-gray-700 hover:text-gray-900 transition-colors flex-shrink-0"
                  aria-label="Clear search">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <button type="button" @click="handleMobileSearch"
                  class="p-2 bg-green-600 text-white hover:bg-green-700 transition-colors flex-shrink-0"
                  aria-label="Search">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <LanguageSelectorMobile :isOpen="showMobileMenu" @close="showMobileMenu = false" />

          <!-- Main Menu -->
          <div v-if="!showCategoriesView">
            <button @click="showCategoriesView = true"
              :class="['w-full flex items-center justify-between px-4 py-2 text-left transition-colors font-marketing', isActive('/books') ? 'text-green-700 border-l-4 border-green-700 bg-creme-100 font-semibold' : 'text-primary hover:bg-creme-100']">
              <span>Books</span>
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <a href="/authors" @click="showMobileMenu = false"
              :class="['block px-4 py-2 text-left transition-colors font-marketing', isActive('/authors') ? 'text-green-700 border-l-4 border-green-700 bg-creme-100 font-semibold' : 'text-primary hover:bg-creme-100']">
              Authors
            </a>
            <a href="/events" @click="showMobileMenu = false"
              :class="['block px-4 py-2 text-left transition-colors font-marketing', isActive('/events') ? 'text-green-700 border-l-4 border-green-700 bg-creme-100 font-semibold' : 'text-primary hover:bg-creme-100']">
              Events
            </a>
            <a href="/submissions" @click="showMobileMenu = false"
              :class="['block px-4 py-2 text-left transition-colors font-marketing', isActive('/submissions') ? 'text-green-700 border-l-4 border-green-700 bg-creme-100 font-semibold' : 'text-primary hover:bg-creme-100']">
              Submissions
            </a>
            <a href="/press" @click="showMobileMenu = false"
              :class="['block px-4 py-2 text-left transition-colors font-marketing', isActive('/press') ? 'text-green-700 border-l-4 border-green-700 bg-creme-100 font-semibold' : 'text-primary hover:bg-creme-100']">
              Press
            </a>
            <a href="/careers" @click="showMobileMenu = false"
              :class="['block px-4 py-2 text-left transition-colors font-marketing', isActive('/careers') ? 'text-green-700 border-l-4 border-green-700 bg-creme-100 font-semibold' : 'text-primary hover:bg-creme-100']">
              Careers
            </a>
            <a v-if="currentUser?.is_admin" href="/admin" @click="showMobileMenu = false"
              :class="['block px-4 py-2 text-left transition-colors font-marketing', isActive('/admin') ? 'text-green-700 border-l-4 border-green-700 bg-creme-100 font-semibold' : 'text-green-700 font-semibold hover:bg-creme-100']">
              Admin
            </a>
          </div>

          <!-- Categories View -->
          <div v-if="showCategoriesView" class="space-y-2">
            <button @click="showCategoriesView = false"
              class="flex items-center gap-2 px-4 py-2 text-primary hover:bg-creme-100 transition-colors font-marketing">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
              <span>Back</span>
            </button>
            <div class="border-t border-gray-200 pt-2">
              <h3 class="px-4 py-2 text-sm font-marketing font-bold text-gray-500 uppercase tracking-wider">Categories
              </h3>
              <a href="/books?category=all" @click.prevent="handleMobileCategoryClick('all')"
                class="block px-4 py-2 text-primary hover:bg-creme-100 transition-colors font-sans">
                All Books
              </a>
              <a href="/books?category=coming-soon" @click.prevent="handleMobileCategoryClick('coming-soon')"
                class="block px-4 py-2 text-primary hover:bg-creme-100 transition-colors font-sans">
                Coming Soon
              </a>
              <a v-for="category in categories" :key="category.id" :href="`/books?category=${category.slug}`"
                @click.prevent="handleMobileCategoryClick(category.slug)"
                class="block px-4 py-2 text-primary hover:bg-creme-100 transition-colors font-sans">
                {{ category.name }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { authService } from '../services/auth'
import { cartService } from '../services/cart'
import { publicService } from '../services/public'
import LanguageSelector from './LanguageSelector.vue'
import LanguageSelectorMobile from './LanguageSelectorMobile.vue'
import Loader from './Loader.vue'
import { useNavbarAlignment } from '../composables/useNavbarAlignment'

useNavbarAlignment()

const showMobileMenu = ref(false)
const showUserMenu = ref(false)
const showCategoriesView = ref(false)
const isNavigating = ref(false)
const currentUser = ref(authService.getStoredUser())
const currentPage = ref('')
const mobileSearchQuery = ref('')
const cartItemCount = computed(() => cartService.itemCount.value)
const categories = ref([])
const isScrollingDown = ref(false)
const scrollY = ref(0)
let lastScrollY = 0

defineEmits(['open-search'])

const isAuthenticated = computed(() => {
  return authService.isAuthenticated() && currentUser.value !== null
})

const isNavbarHidden = computed(() => isScrollingDown.value && scrollY.value > 100)

const updatePage = () => {
  currentPage.value = window.location.pathname
}

const isActive = (path) => {
  const currentPath = currentPage.value || window.location.pathname
  if (path === '/') {
    return currentPath === '/' || currentPath === ''
  }
  return currentPath === path || currentPath === `${path}/`
}

const closeMenus = (e) => {
  if (!e.target.closest('.relative')) {
    showUserMenu.value = false
  }
}

const loadCategories = async () => {
  try {
    const response = await publicService.getCategories({ limit: 1000 })
    categories.value = response.categories || []
  } catch (error) {
    console.error('Failed to load categories:', error)
  }
}

const logout = () => {
  authService.logout()
  currentUser.value = null
  showUserMenu.value = false
  window.location.href = '/'
}

// const checkAuth = async () => {
//   console.log('calling checkAuth in NavBar')
//   let user = await authService.getStoredUser()
//   if (user) {
//     currentUser.value = user
//     return
//   }

//   user = await authService.getCurrentUser()
//   currentUser.value = user
// }


const handleMobileSearch = () => {
  if (mobileSearchQuery.value.trim()) {
    showMobileMenu.value = false
    window.location.href = `/books?q=${encodeURIComponent(mobileSearchQuery.value.trim())}`
  }
}

const clearMobileSearch = () => {
  mobileSearchQuery.value = ''
}

const handleMobileCategoryClick = (categorySlug) => {
  isNavigating.value = true
  showMobileMenu.value = false
  showCategoriesView.value = false
  window.location.href = `/books?category=${categorySlug}`
}

const handleNavClick = (e) => {
  const link = e.target.closest('a[href^="/"]')
  if (link && link.getAttribute('href') !== '#') {
    setTimeout(() => {
      updatePage()
      syncCart()
    }, 0)
  }
}


const syncCart = async () => {
  const user = currentUser.value || authService.getStoredUser()
  await cartService.loadCart(user?.id)
}

const handleStorageChange = (e) => {
  if (e.key === 'barry_bonna_cart' || e.key === null) {
    syncCart()
  }
}

const handleFocus = () => {
  syncCart()
}

const handleVisibilityChange = () => {
  if (!document.hidden) {
    syncCart()
  }
}

const handleScroll = () => {
  const currentScrollY = window.scrollY
  scrollY.value = currentScrollY

  // Only update scroll direction if we've scrolled more than 10px to avoid jitter
  if (Math.abs(currentScrollY - lastScrollY) > 10) {
    isScrollingDown.value = currentScrollY > lastScrollY
    lastScrollY = currentScrollY
  }
}

watch(showMobileMenu, (isOpen) => {
  document.documentElement.dataset.globalMobileMenuOpen = isOpen ? 'true' : 'false'
  window.dispatchEvent(new CustomEvent('global-mobile-menu-change', {
    detail: { isOpen }
  }))
})

watch(isNavbarHidden, (hidden) => {
  document.documentElement.dataset.globalNavbarHidden = hidden ? 'true' : 'false'
}, { immediate: true })

onMounted(async () => {
  const user = authService.getStoredUser()
  if (!user) {
    const fetchedUser = await authService.getCurrentUser()
    currentUser.value = fetchedUser
  } else {
    currentUser.value = user
  }
  await syncCart()
  await loadCategories()
  updatePage()
  document.addEventListener('click', closeMenus)
  document.addEventListener('click', handleNavClick)
  window.addEventListener('popstate', () => {
    updatePage()
    syncCart()
  })
  window.addEventListener('storage', handleStorageChange)
  window.addEventListener('focus', handleFocus)
  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('scroll', handleScroll)
  lastScrollY = window.scrollY
})

onUnmounted(() => {
  document.documentElement.dataset.globalMobileMenuOpen = 'false'
  document.documentElement.dataset.globalNavbarHidden = 'false'
  document.removeEventListener('click', closeMenus)
  document.removeEventListener('click', handleNavClick)
  window.removeEventListener('popstate', updatePage)
  window.removeEventListener('storage', handleStorageChange)
  window.removeEventListener('focus', handleFocus)
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
.mobile-menu {
  animation: slideInFromLeft 0.3s ease-out;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
}

@keyframes slideInFromLeft {
  from {
    transform: translateX(-100%);
  }

  to {
    transform: translateX(0);
  }
}
</style>
